import React, { useContext, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { FileUploader } from "react-drag-drop-files";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaCloudUploadAlt, FaTable, FaTrash } from "react-icons/fa";
import SelectSearch from "react-select-search";
import { toast } from "react-toastify";
import { read, utils } from "xlsx";

import CustomTable from "../components/customTable";
import { INPUT_TYPE } from "../constants";
import AppContext from "../context/app-context";
import userProfileService from "../services/userProfileService";
import ExpenseTrackingMetadataDB from "../storage/ExpenseTrackingMetadataDB";
import { createDefaultHeaderMapping, delay, getExpenseFieldsFromProfile, getNumberFromStr } from "../utils/helperFunctions";

const BulkAddExpensePage = () => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [headerMappings, setHeaderMappings] = useState({});
  const [error, setError] = useState("");

  const {
    userProfile,
    setLoading,
    loading,
    loadItems = () => {},
    trackingItems = [],
    onlineItems = [],
    loadItemsFromOnline,
  } = useContext(AppContext);
  const fieldConfigs = getExpenseFieldsFromProfile(userProfile);
  const fileTypes = ["XLSX", "XLS"];
  const uploadZoneStyle = {
    border: "3px dashed #6EC3C4",
    borderRadius: "20px",
    padding: "2rem",
    backgroundColor: "#f8f9fa",
    cursor: "pointer",
    transition: "all 0.3s ease",
    ":hover": {
      backgroundColor: "#f0faff",
      borderColor: "#FF7F7F",
    },
  };
  const handleDeleteRow = (row) => {
    const { headers, rows = [] } = excelData;
    const newRows = rows.filter((items) => {
      for (var i = 0; i < row.length; i += 1) {
        if (items[i] !== row[i]) {
          return true;
        }
      }
      return false;
    });
    setExcelData({ headers, rows: newRows });
  };

  const filteredRows = useMemo(() => {
    if (!excelData) return [];
    return excelData.rows;
  }, [excelData]);

  const tableHeaders = useMemo(() => {
    if (!excelData) return [];
    return [...excelData.headers, t("BulkAddExpensePage.deleteColumn")];
  }, [excelData, t]);

  const tableRows = useMemo(() => {
    return filteredRows.map((row, rowIndex) => [
      ...row.map((cellValue, cellIndex) => ({
        value: cellValue,
        onClick: () => console.log("Cell clicked", rowIndex, cellIndex),
      })),
      {
        value: (
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleDeleteRow(row)}
            className="d-flex align-items-center gap-1"
          >
            <FaTrash />
            <span className="d-none d-md-inline">{t("BulkAddExpensePage.deleteButton")}</span>
          </Button>
        ),
        onClick: null,
      },
    ]);
  }, [filteredRows, t]);
  const handleFileUpload = async (file) => {
    setLoading(true);
    setError("");
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];

        // Get Excel date serial numbers and convert to JS Dates
        const jsonData = utils.sheet_to_json(worksheet, {
          header: 1,
          raw: false, // Get formatted strings instead of raw values
          cellDates: true, // Parse dates as JS Date objects
        });

        // Convert Excel dates to YYYY-MM-DD format
        const convertExcelDate = (serial) => {
          const utcDays = Math.floor(serial - 25569);
          const utcValue = utcDays * 86400;
          const dateInfo = new Date(utcValue * 1000);

          // Add 1 day to fix Excel's leap year bug
          dateInfo.setDate(dateInfo.getDate() + 1);

          return dateInfo.toISOString().split("T")[0];
        };

        const headers = jsonData[0];
        const autoHeaderMapping = createDefaultHeaderMapping(headers, fieldConfigs);
        setHeaderMappings(autoHeaderMapping);
        const rows = jsonData.slice(1).map((row) =>
          row.map((cell, index) => {
            // Check if the header column is a date field
            if (headers[index].toLowerCase().includes("date")) {
              // Handle both string dates and serial numbers
              return typeof cell === "number" ? convertExcelDate(cell) : cell;
            }
            return cell;
          })
        );

        setExcelData({ headers, rows });
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      setError("Error reading Excel file. Please check the format.");
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (excelHeader, selected) => {
    setHeaderMappings((prev) => ({
      ...prev,
      [excelHeader]: selected,
    }));
  };
  const submitDisable = loading || !Object.keys(headerMappings).length;
  const mappedData = useMemo(() => {
    if (!excelData || !headerMappings) return [];

    return excelData.rows.map((row) => {
      const mappedRow = {};
      Object.keys(headerMappings).forEach((excelHeader) => {
        const systemField = headerMappings[excelHeader];
        const index = excelData.headers.findIndex((hname) => hname === excelHeader);
        if (systemField) {
          const findField = fieldConfigs.find((item) => item.fieldName === systemField);
          if (findField && findField.inputType === INPUT_TYPE.NUMBER) {
            mappedRow[systemField] = Number(getNumberFromStr(row[index]));
          } else mappedRow[systemField] = row[index];
        }
      });
      return mappedRow;
    });
  }, [excelData, headerMappings]);

  const handleSubmit = async () => {
    const requiredFields = ["total", "purchasedDate"];
    for (const req of requiredFields) {
      if (!Object.values(headerMappings).includes(req)) {
        toast.error("You must include the" + t(`EditableModal.fields.${req}`, req));
        return;
      }
    }
    setLoading(true);
    for (const item of mappedData) {
      await userProfileService.putExpenseEvent({
        updatedItem: item,
        callback: async ({ err, data }) => {
          if (err) {
            toast.error(err);
            return;
          }
          const { expenseResource } = data;
          await ExpenseTrackingMetadataDB.putItemToExpenseTrackingMetadata(expenseResource);

          toast.success(t("expenseTracking.addSuccess", { title: item.title }));
          loadItems();
        },
      });
      await delay(2);
    }
    setLoading(false);
  };
  return (
    <Container className="mt-5 py-4">
      <Row className="justify-content-md-center">
        <Col xs={12} md={10}>
          <div className="text-center mb-4">
            <h1 className="display-5 mb-3">📁 {t("BulkAddExpensePage.title")}</h1>
            <p className="lead text-muted">{t("BulkAddExpensePage.subtitle")} 🐨</p>
          </div>
          {!excelData ? (
            <div className="text-center mb-4">
              <FileUploader
                multiple={false}
                handleChange={handleFileUpload}
                name="file"
                types={fileTypes}
                style={{ width: "100%" }}
              >
                <div style={uploadZoneStyle} className="text-center">
                  <FaCloudUploadAlt size="3em" className="text-primary mb-3" />
                  <h4 className="text-secondary">{t("BulkAddExpensePage.dropArea.mainText")}</h4>
                  <p className="text-muted mb-0">
                    {t("BulkAddExpensePage.dropArea.supportedFormats", {
                      formats: fileTypes.join(", "),
                    })}
                  </p>
                  <div className="mt-2">
                    <img
                      src="/kola2.jpg"
                      alt={t("BulkAddExpensePage.dropArea.imageAlt")}
                      style={{ width: "120px", opacity: 0.8 }}
                    />
                  </div>
                </div>
              </FileUploader>
            </div>
          ) : (
            <>
              <div className="mb-4 p-3 bg-light rounded">
                <h5 className="text-success mb-3">
                  <FaTable className="me-2" />
                  {t("BulkAddExpensePage.mappingSection.title")}
                </h5>

                <Row className="g-3">
                  {excelData.headers.map((header) => (
                    <Col md={6} key={header}>
                      <Form.Group>
                        <Form.Label>{header}</Form.Label>
                        <SelectSearch
                          options={[
                            { name: "", value: "" },
                            ...fieldConfigs.map((fc) => ({
                              value: fc.fieldName,
                              name: t(`EditableModal.fields.${fc.label}`, fc.label),
                            })),
                          ]}
                          onChange={(selected) => handleMappingChange(header, selected)}
                          search={true}
                          value={headerMappings[header]}
                        />
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </div>
              {filteredRows.length > 0 && (
                <div className="mb-4">
                  <h5 className="text-success mb-3">
                    <FaCheckCircle className="me-2" />
                    {t("BulkAddExpensePage.preview.title")}
                  </h5>
                  <CustomTable
                    headers={tableHeaders}
                    colCanSort={[...excelData.headers.map(() => true), false]}
                    rows={tableRows}
                    headerStyle={{
                      backgroundColor: "#6EC3C4",
                      color: "white",
                      borderRadius: "10px",
                    }}
                    rowStyle={(rowIndex) => ({
                      transition: "all 0.3s ease",
                      ":hover": {
                        backgroundColor: "#f0fdf4",
                      },
                    })}
                  />
                </div>
              )}

              <div className="text-center mt-4">
                <Button
                  variant="success"
                  size="lg"
                  onClick={handleSubmit}
                  disabled={submitDisable}
                  style={{
                    background: submitDisable ? "gray" : "linear-gradient(135deg, #6EC3C4 0%, #FF7F7F 100%)",
                    border: "none",
                    borderRadius: "15px",
                    padding: "1rem 2rem",
                  }}
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    t("BulkAddExpensePage.buttons.upload")
                  )}
                </Button>
              </div>
            </>
          )}
          {error && (
            <Alert variant="danger" className="mt-3 rounded-pill">
              {t(`BulkAddExpensePage.errors.${error}`, error)}
            </Alert>
          )}{" "}
        </Col>
      </Row>
    </Container>
  );
};

export default BulkAddExpensePage;
